import { Navigate, useLocation } from 'react-router-dom';
import { inventoryRecordHref } from './library/inventoryTabs';

export type LegacyInventoryKind = 'yearly' | 'catalog' | 'kits' | 'assets' | 'stock' | 'stock-editor' | 'alerts';

const TAB_OF: { [kind in Exclude<LegacyInventoryKind, 'yearly'>]: string } = {
  catalog: 'items',
  kits: 'kits',
  assets: 'assets',
  stock: 'stock',
  'stock-editor': 'stock',
  alerts: 'alerts',
};

function isUuid(value: string | null): value is string {
  return Boolean(value && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value));
}

/**
 * Where an old Inventory link lands now (docs/inventory.md, "Old links"):
 * - `/yearly-inventory` keeps every query parameter (e.g. `?aiReview=1`) on the Yearly requests tab;
 * - `/admin/inventory`, `/admin/kits`, `/admin/assets`, `/admin/stock`, `/admin/alerts` open the Items,
 *   Kits, Assets, Stock and Alerts tabs;
 * - `/admin/assets?assetId=` (stored asset-feedback notifications) opens the asset record;
 * - `/admin/stock/editor?itemId=&locationId=` opens the item record's Stock by site at that site.
 */
export function legacyInventoryTarget(kind: LegacyInventoryKind, search: string): string {
  const params = new URLSearchParams(search);
  if (kind === 'yearly') {
    params.set('tab', 'yearly');
    return `/inventory?${params.toString()}`;
  }
  const assetId = params.get('assetId');
  if (kind === 'assets' && isUuid(assetId)) return inventoryRecordHref.asset(assetId, 'details');
  const itemId = params.get('itemId');
  const siteId = params.get('locationId');
  if (kind === 'stock-editor' && isUuid(itemId)) {
    return `${inventoryRecordHref.item(itemId, 'stock')}${isUuid(siteId) ? `?site=${encodeURIComponent(siteId)}` : ''}`;
  }
  const next = new URLSearchParams({ tab: TAB_OF[kind] });
  if (isUuid(siteId)) next.set('site', siteId);
  return `/inventory?${next.toString()}`;
}

/** Rewrites an old Inventory link to its tab or record, replacing the history entry. */
export default function LegacyInventoryRedirect({ kind }: { kind: LegacyInventoryKind }) {
  const location = useLocation();
  return <Navigate to={`${legacyInventoryTarget(kind, location.search)}${location.hash}`} replace />;
}
